import React, { useState, useMemo, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "./Calender.scss";
import { IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import weekday from "dayjs/plugin/weekday";
dayjs.extend(weekday);

interface CalendarEvent {
  date: string;
  type: "request" | "class";
  data: any;
}

interface CalendarProps {
  events: CalendarEvent[];
  onDayClick?: (events: CalendarEvent[]) => void;
  onMonthChange?: (month: number, year: number) => void;
}

const CalendarView: React.FC<CalendarProps> = ({
  events,
  onDayClick,
  onMonthChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Generate 6 weeks (42 days), Monday start
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");

    const startDate = startOfMonth.startOf("week"); // Monday
    const endDate = endOfMonth.endOf("week");       // Sunday

    const days: dayjs.Dayjs[] = [];
    let day = startDate;

    while (day.isBefore(endDate) || day.isSame(endDate, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }

    while (days.length < 42) {
      days.push(day);
      day = day.add(1, "day");
    }

    return days;
  }, [currentMonth]);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach((ev) => {
      const key = dayjs(ev.date).format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  // Scroll to the 1st day of current month after render
  const scrollToFirstDay = () => {
    const firstDay = currentMonth.startOf("month");
    const firstDayKey = firstDay.format("YYYY-MM-DD");

    const index = calendarDays.findIndex((d) => d.format("YYYY-MM-DD") === firstDayKey);

    if (index !== -1 && dayRefs.current[index]) {
      dayRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center",    // Centers the 1st day vertically
        inline: "center",   // Centers horizontally (if grid is wide)
      });

      // Optional: Add a nice highlight pulse
      dayRefs.current[index]?.classList.add("highlight-pulse");
      setTimeout(() => {
        dayRefs.current[index]?.classList.remove("highlight-pulse");
      }, 1000);
    }
  };

  const goToPreviousMonth = () => setCurrentMonth(currentMonth.subtract(1, "month"));
  const goToNextMonth = () => setCurrentMonth(currentMonth.add(1, "month"));

  // Trigger scroll to 1st day whenever month changes
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(scrollToFirstDay, 100);

    onMonthChange?.(currentMonth.month() + 1, currentMonth.year());

    return () => clearTimeout(timer);
  }, [currentMonth]);

  const today = dayjs().format("YYYY-MM-DD");

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <Typography sx={{ fontSize: "14px", color: "#009DDF", fontWeight: "600" }}>
          Calendar View
        </Typography>

        <h3>{currentMonth.format("MMMM YYYY")}</h3>

        <div>
          <IconButton size="small" onClick={goToPreviousMonth}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={goToNextMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="calendar-week-row">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="week-name">{d}</div>
        ))}
      </div>

      <div className="calendar-grid-wrapper">
        <div className="calendar-grid">
          {calendarDays.map((day, index) => {
            const dateKey = day.format("YYYY-MM-DD");
            const dayEvents = eventsByDate[dateKey] || [];
            const isCurrentMonth = day.month() === currentMonth.month();
            const isToday = dateKey === today;
            const isFirstDay = day.date() === 1 && isCurrentMonth;
            const status = dayEvents[0]?.data?.printing_status || "";

            return (
              <div
                key={index}
                ref={(el) => (dayRefs.current[index] = el)}
                className={`
                  calendar-day
                  ${!isCurrentMonth ? "inactive" : ""}
                  ${dayEvents.length > 0 ? "has-event" : ""}
                  ${isToday ? "today" : ""}
                  ${isFirstDay ? "first-day" : ""}
                  ${status ? `status-${status.toLowerCase()}` : ""}
                `}
                onClick={() => dayEvents.length > 0 && onDayClick?.(dayEvents)}
              >
                <span className="date-number">{day.date()}</span>

                {/* {dayEvents.length > 0 && (
                  <div className="event-indicator">
                    {dayEvents.length > 1 ? dayEvents.length : "●"}
                  </div>
                )} */}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;